import { Spinner } from '@blueprintjs/core';
import * as React from 'react';

import {
  DataSourceTable,
  DataSourceTableSelectItem,
} from 'src/constants/dataSourceTable';
import { ProjectVirtualGraphsToCreate } from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectVirtualGraphPanelScope } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelScope';
import { getCustomQueryFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getCustomQueryFromVgsToCreate';
import { getTablesFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getTablesFromVgsToCreate';
import { sortVirtualGraphs } from 'src/containers/ProjectVirtualGraphPanel/utils/sortVirtualGraphs';
import { DataSource } from 'src/store/state/dataSources';
import { generateDateRandomId } from 'src/utils/generateId';

type QueueItem = {
  from: DataSourceTableSelectItem | string;
  virtualGraphId: string;
};

export type ProjectVirtualGraphPanelNewScopeContainerProps = {
  addItemToQueue: (isExisting?: boolean, ...queueItems: QueueItem[]) => void;
  dataSource: DataSource | null;
  disableEditing?: boolean;
  handleRemoveVirtualGraph?: (id: string) => void;
  isPendingTables?: boolean;
  isUsingCustomQuery: boolean;
  setIsUsingCustomQuery: (isUsingCustomQuery: boolean) => void;
  setSelectedTab: (selectedTab: string) => void;
  showError: boolean;
  tables: DataSourceTable[];
  virtualGraphsToCreate: ProjectVirtualGraphsToCreate;
};

export const ProjectVirtualGraphPanelNewScopeContainer: React.VFC<
  ProjectVirtualGraphPanelNewScopeContainerProps
> = ({
  addItemToQueue,
  dataSource,
  disableEditing,
  handleRemoveVirtualGraph,
  isPendingTables,
  isUsingCustomQuery,
  setIsUsingCustomQuery,
  setSelectedTab,
  showError,
  tables,
  virtualGraphsToCreate,
}) => {
  const [customQueryText, setCustomQueryText] = React.useState<string>(
    (getCustomQueryFromVgsToCreate(virtualGraphsToCreate)?.from as string) || ''
  );

  const handleSelectTable = React.useCallback(
    (tableItem: DataSourceTableSelectItem) => {
      const selectedTableNames: string[] = [];
      getTablesFromVgsToCreate(virtualGraphsToCreate).forEach(
        (virtualGraphToCreate) => {
          const table = virtualGraphToCreate.from as DataSourceTableSelectItem;
          selectedTableNames.push(table.name);
        }
      );

      // Deselect if item already selected
      if (selectedTableNames.includes(tableItem.name)) {
        const virtualGraphToRemove = getTablesFromVgsToCreate(
          virtualGraphsToCreate
        ).find((virtualGraphToCreate) => {
          const table = virtualGraphToCreate.from as DataSourceTableSelectItem;
          return table.name === tableItem.name;
        });

        handleRemoveVirtualGraph?.(virtualGraphToRemove?.id as string);
      } else {
        addItemToQueue(false, {
          virtualGraphId: generateDateRandomId(),
          from: tableItem,
        });
      }
    },
    [addItemToQueue, handleRemoveVirtualGraph, virtualGraphsToCreate]
  );

  const handleCustomQueryTextChange = React.useCallback((customText) => {
    setCustomQueryText(customText);
  }, []);

  // Restore the appropriate running query when the user toggles between modes
  const handleIsUsingCustomQueryChange = React.useCallback(
    (isUsingCustomQuery: boolean) => {
      setIsUsingCustomQuery(isUsingCustomQuery);

      const virtualGraphsArray = Object.values(virtualGraphsToCreate);
      const virtualGraphs = virtualGraphsArray.filter(
        (virtualGraphToCreate) => typeof virtualGraphToCreate.from === 'object'
      );
      const customQuery = virtualGraphsArray.filter(
        (vg) => typeof vg.from === 'string'
      )[0];

      if (isUsingCustomQuery) {
        if (customQuery) {
          setSelectedTab(customQuery.id);
        } else {
          setSelectedTab('');
        }
      } else if (virtualGraphs.length) {
        setSelectedTab(sortVirtualGraphs(virtualGraphsToCreate)[0].id);
      } else {
        setSelectedTab('');
      }
    },
    [setIsUsingCustomQuery, setSelectedTab, virtualGraphsToCreate]
  );

  const validateCustomQuery = React.useCallback(
    (text: string) => {
      const currentCustomVirtualGraph = Object.values(
        virtualGraphsToCreate
      ).find((virtualGraph) => typeof virtualGraph.from === 'string');
      addItemToQueue(false, {
        from: text,
        virtualGraphId: currentCustomVirtualGraph?.id || generateDateRandomId(),
      });
    },
    [addItemToQueue, virtualGraphsToCreate]
  );

  return isPendingTables ? (
    <Spinner className="sd-project-virtual-graph-panel__resource-scope" />
  ) : (
    <ProjectVirtualGraphPanelScope
      customQueryText={customQueryText}
      disableEditing={disableEditing}
      handleCustomQueryTextChange={handleCustomQueryTextChange}
      handleIsUsingCustomQueryChange={handleIsUsingCustomQueryChange}
      handleRemoveVirtualGraph={handleRemoveVirtualGraph}
      handleSelectTable={handleSelectTable}
      isUsingCustomQuery={isUsingCustomQuery}
      showError={showError}
      tableMetadataErrorResponse={
        dataSource?.tableMetadataErrorResponse || null
      }
      tables={tables}
      validateCustomQuery={validateCustomQuery}
      virtualGraphsToCreate={virtualGraphsToCreate}
    />
  );
};
